// src/services/system-admin/transport-payments.service.ts
import { API_BASE_URL } from "@/lib/api";
import type {
  PagedResult,
  PaymentListItem,
  PaymentDetail,
  GetPaymentsParams,
  CreatePaymentLinkRequest,
  PaymentLink,
  CancelPaymentLinkRequest,
  VerifyPaymentRequest,
  RecordOfflinePaymentRequest,
  TransportDashboardSummary,
} from "@/types/admin/transport-payment-type";

function authHeaders(): HeadersInit {
  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("adminAccessToken") || ""
      : "";
  return {
    Accept: "application/json, text/plain, */*",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handle<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    let message = fallback;
    try {
      const errData = await res.json();
      message = errData?.title || errData?.message || errData?.error || fallback;
    } catch {
      // response wasn't JSON — keep fallback
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

// The real API returns raw fields (`uid`, `paymentReference`, `creationDate`)
// that don't match our PaymentListItem shape (`paymentUid`, `reference`,
// `createdAt`), and no `customerName`/`paymentType` fields exist at all.
// Normalize here so the rest of the app can rely on PaymentListItem as-is.
function mapPayment(raw: any): PaymentListItem {
  return {
    paymentUid: raw.uid,
    bookingUid: raw.bookingUid,
    customerId: raw.customerId ?? null,
    customerName:
      raw.customerName ?? (raw.customerId ? `Customer #${raw.customerId}` : null),
    amount: raw.amount,
    currency: raw.currency ?? null,
    paymentType:
      raw.paymentType ??
      (typeof raw.description === "string"
        ? raw.description.split(".")[0]
        : null),
    status: raw.status,
    reference: raw.paymentReference ?? raw.reference ?? null,
    createdAt: raw.creationDate ?? raw.createdAt ?? null,
    paidAt: raw.paidAt ?? null,
  };
}

export const transportPaymentsService = {
  /** GET /api/admin/transport/payments */
  async getPayments(
    params: GetPaymentsParams,
  ): Promise<PagedResult<PaymentListItem>> {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.search) query.set("search", params.search);
    if (params.bookingUid) query.set("bookingUid", params.bookingUid);
    if (params.customerId) query.set("customerId", String(params.customerId));
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);
    query.set("page", String(params.page ?? 1));
    query.set("pageSize", String(params.pageSize ?? 20));

      const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/payments?${query.toString()}`,
      { method: "GET", headers: authHeaders() },
    );
    // API returns a bare array, not { items, totalCount, page, pageSize } —
    // wrap it into the paged shape the rest of the app expects.
    const raw = await handle<any>(res, "Failed to load payments.");
    const items = Array.isArray(raw) ? raw : (raw?.items ?? []);
    return {
      items: items.map(mapPayment),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
      totalCount: raw?.totalCount ?? items.length,
    };
  },

  /** GET /api/admin/transport/payments/{paymentUid} */
  async getPaymentById(paymentUid: string): Promise<PaymentDetail> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/payments/${paymentUid}`,
      { method: "GET", headers: authHeaders() },
    );
    const raw = await handle<any>(res, "Failed to load payment details.");
    // Keep any extra raw fields (paymentUrl, attempts, etc.) but make sure
    // the normalized names win over the raw ones.
    return { ...raw, ...mapPayment(raw) } as PaymentDetail;
  },

  /** POST /api/admin/transport/payments/{paymentUid}/verify */
  async verifyPayment(
    paymentUid: string,
    payload: VerifyPaymentRequest,
  ): Promise<PaymentDetail> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/payments/${paymentUid}/verify`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<PaymentDetail>(res, "Failed to verify payment.");
  },

  /** POST /api/admin/transport/bookings/{bookingUid}/payment-links */
  async createPaymentLink(
    bookingUid: string,
    payload: CreatePaymentLinkRequest,
  ): Promise<PaymentLink> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/payment-links`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<PaymentLink>(res, "Failed to create payment link.");
  },

  /** GET /api/admin/transport/bookings/{bookingUid}/payment-links */
  async getPaymentLinks(bookingUid: string): Promise<PaymentLink[]> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/payment-links`,
      { method: "GET", headers: authHeaders() },
    );
    return handle<PaymentLink[]>(res, "Failed to load payment links.");
  },

  /** POST /api/admin/transport/bookings/{bookingUid}/payment-links/regenerate */
  async regeneratePaymentLink(bookingUid: string): Promise<PaymentLink> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/payment-links/regenerate`,
      { method: "POST", headers: authHeaders() },
    );
    return handle<PaymentLink>(res, "Failed to regenerate payment link.");
  },

  /** PATCH /api/admin/transport/payment-links/{paymentUid}/cancel */
  async cancelPaymentLink(
    paymentUid: string,
    payload: CancelPaymentLinkRequest,
  ): Promise<PaymentLink> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/payment-links/${paymentUid}/cancel`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<PaymentLink>(res, "Failed to cancel payment link.");
  },

  /** POST /api/admin/transport/bookings/{bookingUid}/offline-payments */
  async recordOfflinePayment(
    bookingUid: string,
    payload: RecordOfflinePaymentRequest,
  ): Promise<PaymentDetail> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/transport/bookings/${bookingUid}/offline-payments`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      },
    );
    return handle<PaymentDetail>(res, "Failed to record offline payment.");
  },

  /** GET /api/admin/transport/dashboard */
  async getDashboard(): Promise<TransportDashboardSummary> {
    const res = await fetch(`${API_BASE_URL}/api/admin/transport/dashboard`, {
      method: "GET",
      headers: authHeaders(),
    });
    return handle<TransportDashboardSummary>(res, "Failed to load dashboard.");
  },
};